'use client';

import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePathname, useRouter } from 'next/navigation';
import api from '@/utils/api';
import { AlertTriangle, LogOut } from 'lucide-react';
import Link from 'next/link';

export default function RejectedGuard({ children }: { children: React.ReactNode }) {
    const { user, logout } = useAuth();
    const pathname = usePathname();
    const [status, setStatus] = useState<string | null>(null);
    const [rejectionReason, setRejectionReason] = useState<string | null>(null);
    const [checking, setChecking] = useState(false);

    useEffect(() => {
        if (user && user.role !== 'admin') {
            setChecking(true);
            api.get('/verifications/status')
                .then((res: { data: { ok: boolean; status: string; verification?: { reason?: string } } }) => {
                    if (res.data.ok) {
                        setStatus(res.data.status);
                        if (res.data.verification?.reason) {
                            setRejectionReason(res.data.verification.reason);
                        }
                    }
                })
                .catch((err: unknown) => {
                    console.error("RejectedGuard: API Error:", err);
                })
                .finally(() => {
                    setChecking(false);
                });
        }
    }, [user, pathname]); // Re-check on nav change to be safe, or just once per user session could be enough, but pathname ensures we catch them if they try to browse

    // Allow access to:
    // 1. Get verified page (to fix the issue)
    // 2. Auth pages (login/register/etc - covered by !user check usually, but good to be safe)
    // 3. Admin pages (if they somehow have access, though role check above handles that)
    const isAllowedPath =
        pathname?.startsWith('/get-verified') ||
        pathname?.startsWith('/auth') ||
        pathname?.startsWith('/admin');

    if (user && status === 'rejected' && !isAllowedPath) {
        return (
            <div className="fixed inset-0 z-50 bg-gray-50 flex flex-col items-center justify-center p-4">
                <div className="bg-white max-w-lg w-full rounded-2xl shadow-xl p-8 text-center border border-red-100">
                    <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <AlertTriangle className="w-10 h-10 text-red-600" />
                    </div>

                    <h1 className="text-2xl font-bold text-gray-900 mb-3">
                        Account Access Restricted
                    </h1>

                    <p className="text-gray-600 mb-6">
                        Your verification documents were rejected. To continue using Yunimall, you must update your identification documents.
                    </p>

                    {rejectionReason && (
                        <div className="bg-red-50 p-4 rounded-xl mb-8 border border-red-100">
                            <p className="text-xs uppercase tracking-wide text-red-600 font-bold mb-1">
                                Rejection Reason
                            </p>
                            <p className="text-red-900 font-medium">
                                {rejectionReason}
                            </p>
                        </div>
                    )}

                    <div className="flex flex-col gap-3">
                        <Link
                            href="/get-verified"
                            className="w-full py-3 px-6 bg-[#051449] text-white font-bold rounded-xl hover:bg-blue-900 transition-colors"
                        >
                            Update Documents Now
                        </Link>

                        <button
                            onClick={() => logout()}
                            className="w-full py-3 px-6 bg-white text-gray-600 font-medium rounded-xl hover:bg-gray-50 border border-gray-200 transition-colors flex items-center justify-center gap-2"
                        >
                            <LogOut className="w-4 h-4" />
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return <>{children}</>;
}
